// ── EVIDENCE P1.4 — a control's captured evidence, rendered honestly ───────────────────────────
//
// ⚠ THE THREE SIGNALS STAY APART. Recency (advice from the catalogue's cadence), change-since-capture
//   (a fact from an integrity check) and connector health (can we still reach the library) are three
//   different questions, and this panel answers them in three places. Merging any two — a single
//   green tick, a single "stale" badge — is what would make a compliance surface lie.
//
// ⚠ NOTHING HERE SAYS "VERIFIED". The integrity signal is a proprietary, non-cryptographic checksum;
//   the wording comes from @grc/shared so the surface and the capture engine cannot drift.

import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, FileText, ShieldQuestion } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CARD_SURFACE } from '@/components/compliance/card-surface';
import { useControlEvidenceArtifacts, useIntegrityHistory } from '@/api/hooks/useEvidenceArtifacts';
import type { EvidenceArtifact } from '@/api/types/evidence-artifacts.types';
import {
  changeLabelFor, healthLabelFor, HEALTH_NEVER_CHECKED, NO_EVIDENCE, NO_EVIDENCE_DETAIL,
  RECENCY_LABEL, SOURCE_LABEL, capturedLine, integrityClaim, integrityLine, recencyLabel,
  CAPTURED_BY_VERIGRC, captureActor, unavailableKind,
} from './evidence-copy';

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—';

const CHIP = 'rounded-[5px] px-[7px] py-[2px] text-[11px] font-medium';

function RecencyChip({ a }: { a: EvidenceArtifact }) {
  const label = recencyLabel(a);
  // ⚠ "No recommendation" is NOT a warning and NOT a pass — it is the absence of advice, so it is
  //   toned as neither.
  const tone = label === RECENCY_LABEL.due ? 'bg-cert-warn-bg text-cert-warn-fg'
    : label === RECENCY_LABEL.none ? 'bg-bg-surface-2 text-text-faint'
    : 'bg-bg-surface-2 text-text-secondary';
  return <span className={cn(CHIP, tone)}>{label}</span>;
}

// ⚠ repoKind RIDES DOWN, IT IS NOT RE-FETCHED. The trail belongs to ONE artifact, so its
//   provider is the artifact's; a second lookup here could disagree with the row above it.
function IntegrityTrail({ artifactId, repoKind }: { artifactId: string; repoKind: string | null }) {
  const { data, isLoading } = useIntegrityHistory(artifactId);
  if (isLoading) return <p className="mt-[6px] text-[11.5px] text-text-faint">Reading the check history…</p>;
  const events = data?.events ?? [];
  if (events.length === 0) return <p className="mt-[6px] text-[11.5px] text-text-faint">No checks recorded.</p>;
  return (
    <ul className="mt-[6px] space-y-[4px]">
      {events.map((e, i) => (
        <li key={`${e.observed_at}-${i}`} className="text-[11.5px] text-text-secondary">
          <span className="text-text-primary">{changeLabelFor(e.change_kind, repoKind)}</span>
          {' · '}{fmt(e.observed_at)}
          {/* ⚠ ERI S2 C1 — a CAPTURE row names its actor from PRESENCE, never from a missing name.
              A check row shows a name when there is one, and nothing otherwise (unchanged). */}
          {e.change_kind === 'captured'
            ? (() => { const who = captureActor(e.checked_by_platform, e.checked_by_name);
                       return ` · ${who.kind === 'named_user' ? who.name : who.phrase}`; })()
            : e.checked_by_name ? ` · ${e.checked_by_name}` : ''}
          {/* ⚠ The check's own basis, carried verbatim — this is where a WEAKER basis stays visible. */}
          {e.basis && <span className="block text-text-faint">{e.basis}</span>}
        </li>
      ))}
    </ul>
  );
}

/**
 * ⚠ CAPR S2 — EXPORTED, NOT FORKED. The assessment screen lists the same evidence with the same
 * flags, so it renders THIS row (one implementation of the change / availability / recency logic)
 * and adds only what selection needs: a checkbox, a marker, and a slot below for the note. With none
 * of those props the row renders exactly as it always has.
 */
export function ArtifactRow({ a, select, marker, children }: {
  a: EvidenceArtifact;
  select?: { checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean };
  marker?: string | null;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const changed = a.review_state === 'requires_re_review' || a.changed_since_capture;
  // ⚠ ERI S2 (D-ERI-8) — ITS OWN PREDICATE, READ FROM THE PROJECTION. A file that could not be read
  //   is not a content change and never says "re-review required".
  const unavailable = unavailableKind(a.availability);
  // The change line names a CHANGE. When the newest check is an unreadable one, the change it
  //   reports is the earlier content change behind the review flag.
  const latestKind = a.latest_check?.change_kind ?? null;
  const changeKind = latestKind && latestKind !== 'inaccessible' && latestKind !== 'deleted_at_source'
    ? latestKind : 'content_changed';

  return (
    <div className="border-b border-border-subtle px-[16px] py-[13px] last:border-b-0"
         data-testid={select ? 'selectable-artifact' : undefined}>
      <div className="flex flex-wrap items-baseline gap-[10px]">
        {select ? (
          <input type="checkbox" checked={select.checked} disabled={select.disabled}
            onChange={(e) => select.onChange(e.target.checked)} aria-label={`Rely on ${a.title}`}
            className="mt-[3px] h-[13px] w-[13px] shrink-0 accent-accent-cyan" />
        ) : (
          <FileText className="mt-[3px] h-[13px] w-[13px] shrink-0 text-text-faint" aria-hidden="true" />
        )}
        <span className="min-w-0 flex-1 text-[13.5px] font-medium text-text-primary">{a.title}</span>
        {marker && (
          <span className={cn(CHIP, 'bg-sky-500/12 text-sky-300')} data-testid="prior-basis-marker">{marker}</span>
        )}
        {/* ⚠ The source badge exists from day one so no row is ever unlabelled — the Fabric seam. */}
        <span className={cn(CHIP, 'bg-bg-surface-2 text-text-secondary')}>{SOURCE_LABEL[a.source_type]}</span>
        <RecencyChip a={a} />
      </div>

      {/* ⚠ CHANGE IS ITS OWN LINE, never folded into the recency chip. */}
      {changed && (
        <p className="mt-[6px] flex items-center gap-[6px] text-[12px] font-medium text-cert-warn-fg"
           data-testid="changed-since-capture">
          <AlertTriangle className="h-[12px] w-[12px]" aria-hidden="true" />
          {changeLabelFor(changeKind, a.repo_kind)}
        </p>
      )}

      {unavailable && (
        <p className="mt-[6px] flex items-center gap-[6px] text-[12px] font-medium text-cert-warn-fg"
           data-testid="unavailable-at-last-check">
          <AlertTriangle className="h-[12px] w-[12px]" aria-hidden="true" />
          {changeLabelFor(unavailable, a.repo_kind)}
        </p>
      )}

      <p className="mt-[5px] text-[11.5px] text-text-secondary" data-testid="captured-line">
        {capturedLine(a)}
      </p>

      {/* ⚠ TWO DATES, TWO FACTS. "(as captured)" is load-bearing: produced_at is what the provider
          said WHEN WE CAPTURED IT, not the file's current modified date. */}
      <p className="mt-[3px] text-[11.5px] text-text-faint" data-testid="produced-line">
        {/* ⚠ ERI S2 C1 — the actor is decided from PRESENCE. A named user renders as it always has;
            a user with no name is "captured by a user", never VeriGRC. */}
        Produced {fmt(a.produced_at)} (as captured) · {(() => {
          const who = captureActor(a.captured_by_platform, a.captured_by_name);
          return who.kind === 'named_user'
            ? `${CAPTURED_BY_VERIGRC} ${fmt(a.captured_at)} · ${who.name}`
            : `${who.phrase} ${fmt(a.captured_at)}`;
        })()}
      </p>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-[6px] inline-flex items-center gap-[4px] text-[11.5px] text-text-secondary hover:text-text-primary"
      >
        {open ? <ChevronDown className="h-[11px] w-[11px]" aria-hidden="true" />
              : <ChevronRight className="h-[11px] w-[11px]" aria-hidden="true" />}
        Integrity
      </button>
      {open && (
        <div className="mt-[4px] rounded-[7px] border border-border-subtle bg-bg-surface-2 p-[10px]">
          <p className="text-[11.5px] text-text-primary" data-testid="integrity-line">{integrityLine(a)}</p>
          {a.content_hash && (
            <p className="mt-[3px] break-all font-mono text-[11px] text-text-faint">{a.content_hash}</p>
          )}
          {/* ⚠ The full claim for THIS artifact, verbatim from @grc/shared. A file with no hash gets
              the version-only sentence — never the checksum one, which would describe a comparison
              that did not happen. */}
          <p className="mt-[6px] text-[11px] text-text-secondary" data-testid="integrity-claim">
            {integrityClaim(a)}
          </p>
          <IntegrityTrail artifactId={a.artifact_id} repoKind={a.repo_kind} />
        </div>
      )}
      {children}
    </div>
  );
}

export function CapturedEvidencePanel({ reference }: { reference: string }) {
  const { data, isLoading, isError } = useControlEvidenceArtifacts(reference);

  if (isLoading) {
    return <p className="px-[16px] py-[20px] text-[13px] text-text-faint">Reading captured evidence…</p>;
  }
  if (isError || !data) {
    return <p className="px-[16px] py-[20px] text-[13px] text-cert-warn-fg">The captured evidence could not be read.</p>;
  }

  const artifacts = data.artifacts;

  // ⚠ CONNECTOR HEALTH SITS ABOVE THE LIST, because it qualifies EVERY row below it: if we cannot
  //   reach the library, none of this can be re-checked. Read from the first artifact's binding —
  //   a control's evidence lives in one library.
  const health = artifacts[0] ?? null;

  return (
    <div className={cn(CARD_SURFACE, 'overflow-hidden')}>
      {health && (
        <div className="flex items-center gap-[8px] border-b border-border-subtle px-[16px] py-[10px]"
             data-testid="evidence-health">
          <ShieldQuestion className="h-[13px] w-[13px] text-text-faint" aria-hidden="true" />
          <span className="text-[12px] text-text-secondary">
            {/* ⚠ NULL IS NOT HEALTHY. "Not checked yet" is the honest reading of a binding nobody
                has probed — a blank here would read as fine. */}
            {health.health_state === null
              ? HEALTH_NEVER_CHECKED
              : healthLabelFor(health.health_state, health.repo_kind)}
            {health.health_checked_at && ` · ${fmt(health.health_checked_at)}`}
          </span>
        </div>
      )}

      {artifacts.length === 0 ? (
        <div className="px-[16px] py-[20px]" data-testid="evidence-empty">
          {/* ⚠ THE HONEST REASON. Not "no repository connected": connecting one would not populate
              this either, because nothing captures yet. What is true is simply that there is none. */}
          <p className="text-[13px] italic text-text-faint">{NO_EVIDENCE}</p>
          <p className="mt-[4px] text-[12px] text-text-secondary">{NO_EVIDENCE_DETAIL}</p>
        </div>
      ) : (
        artifacts.map((a) => <ArtifactRow key={a.artifact_id} a={a} />)
      )}
    </div>
  );
}
